/**
 * FloatingNotification.ts
 *
 * A reusable floating notification system that:
 * - Can attach to a target object and follow it
 * - Can appear at a fixed position
 * - Intelligently repositions based on screen edges
 * - Supports multiple notification types with different styles
 * - Animates with float and fade effects
 */

import Phaser from 'phaser';
import { LayerDepths } from '../constants/enums';

export type NotificationType = 'error' | 'warning' | 'info' | 'success' | 'biomatter' | 'powerup' | 'damage';

export interface FloatingNotificationConfig {
  /** The text to display */
  text: string;
  /** Notification type for styling */
  type?: NotificationType;
  /** Target object to follow (if provided, ignores position) */
  target?: Phaser.GameObjects.GameObject & { x: number; y: number };
  /** Fixed position (used if no target) */
  position?: { x: number; y: number };
  /** Offset from target/position */
  offset?: { x: number; y: number };
  /** Duration before fade out (ms) */
  duration?: number;
  /** Font size in pixels */
  fontSize?: number;
  /** Whether to use HUD mode (scrollFactor 0) */
  isHUD?: boolean;
  /** Auto-calculate duration based on text length */
  autoDuration?: boolean;
}

interface NotificationStyle {
  textColor: string;
  strokeColor: string;
  bgColor?: number;
  bgAlpha?: number;
}

const NOTIFICATION_STYLES: Record<NotificationType, NotificationStyle> = {
  error: {
    textColor: '#000000',
    strokeColor: '#FFFFFF',
    bgColor: 0xff4444,
    bgAlpha: 0.75,
  },
  warning: {
    textColor: '#000000',
    strokeColor: '#FFFFFF',
    bgColor: 0xffcc00,
    bgAlpha: 0.75,
  },
  info: {
    textColor: '#000000',
    strokeColor: '#FFFFFF',
    bgColor: 0x4488ff,
    bgAlpha: 0.75,
  },
  success: {
    textColor: '#000000',
    strokeColor: '#FFFFFF',
    bgColor: 0x44dd66,
    bgAlpha: 0.75,
  },
  // Game notification types (no background)
  biomatter: {
    textColor: '#FFD700', // Gold
    strokeColor: '#000000',
  },
  powerup: {
    textColor: '#00FF88', // Green
    strokeColor: '#000000',
  },
  damage: {
    textColor: '#FF4444', // Red
    strokeColor: '#000000',
  },
};

// Constants for auto-duration calculation
// Reference: "$50" (3 chars) = ~2000ms
const AUTO_DURATION = {
  BASE_MS: 1000,
  MS_PER_CHAR: 333,
  MIN_MS: 1500,
  MAX_MS: 8000,
};

const DEFAULT_CONFIG = {
  type: 'info' as NotificationType,
  offset: { x: 0, y: -40 },
  duration: 2500,   // Total time visible (hold + fade)
  holdTime: 1000,   // Time to stay fully visible before fading
  fontSize: 18,
  isHUD: false,
  autoDuration: false,
};

export class FloatingNotification extends Phaser.GameObjects.Container {
  private config: Required<Omit<FloatingNotificationConfig, 'target' | 'position'>> & {
    target?: FloatingNotificationConfig['target'];
    position?: FloatingNotificationConfig['position'];
  };
  private background: Phaser.GameObjects.Graphics;
  private textObj: Phaser.GameObjects.Text;
  private fadeOutTween?: Phaser.Tweens.Tween;
  private updateEvent?: Phaser.Time.TimerEvent;

  // Direction of float animation (calculated based on edge detection)
  private floatDirection: { x: number; y: number } = { x: 0, y: -1 };

  constructor(scene: Phaser.Scene, config: FloatingNotificationConfig) {
    // Calculate initial position
    const initialPos = config.target
      ? { x: config.target.x, y: config.target.y }
      : config.position ?? { x: scene.scale.width / 2, y: scene.scale.height / 2 };

    super(scene, initialPos.x, initialPos.y);

    // Merge with defaults
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
      offset: { ...DEFAULT_CONFIG.offset, ...config.offset },
    };

    // Calculate auto-duration if enabled
    if (this.config.autoDuration) {
      const calculatedDuration = AUTO_DURATION.BASE_MS + config.text.length * AUTO_DURATION.MS_PER_CHAR;
      this.config.duration = Math.max(
        AUTO_DURATION.MIN_MS,
        Math.min(AUTO_DURATION.MAX_MS, calculatedDuration)
      );
    }

    // Create background
    this.background = scene.add.graphics();
    this.add(this.background);

    // Create text with style based on type
    const style = NOTIFICATION_STYLES[this.config.type];
    this.textObj = scene.add.text(0, 0, this.config.text, {
      fontFamily: 'Arial',
      fontSize: `${this.config.fontSize}px`,
      fontStyle: 'bold',
      color: style.textColor,
      stroke: style.strokeColor,
      strokeThickness: 4,
      padding: { x: 8, y: 4 },
    });
    this.textObj.setOrigin(0.5, 0.5);
    this.add(this.textObj);

    // Draw background
    this.drawBackground(style);

    // Apply offset and calculate direction based on edges
    this.calculatePositionAndDirection();

    // Set depth
    this.setDepth(LayerDepths.HUD + 10);

    // HUD mode (fixed on screen)
    if (this.config.isHUD) {
      this.setScrollFactor(0);
    }

    // Add to scene
    scene.add.existing(this);

    // Start following target if provided
    if (this.config.target) {
      this.startFollowing();
    }

    // Start animation
    this.startAnimation();
  }

  /**
   * Draw background rectangle behind text
   */
  private drawBackground(style: NotificationStyle): void {
    if (style.bgColor === undefined) return;

    const padding = 10;
    const cornerRadius = 12;
    const width = this.textObj.width + padding * 2;
    const height = this.textObj.height + padding * 2;

    this.background.clear();
    this.background.fillStyle(style.bgColor, style.bgAlpha ?? 0.75);
    this.background.fillRoundedRect(
      -width / 2,
      -height / 2,
      width,
      height,
      cornerRadius
    );
  }

  /**
   * Calculate position with offset and determine float direction based on screen edges
   */
  private calculatePositionAndDirection(): void {
    const camera = this.scene.cameras.main;
    const margin = 50; // Distance from edge to trigger repositioning

    // Get screen bounds (in world coordinates)
    const screenLeft = camera.scrollX;
    const screenRight = camera.scrollX + camera.width;
    const screenTop = camera.scrollY;
    const screenBottom = camera.scrollY + camera.height;

    // If HUD mode, use fixed screen coordinates
    const bounds = this.config.isHUD
      ? { left: 0, right: camera.width, top: 0, bottom: camera.height }
      : { left: screenLeft, right: screenRight, top: screenTop, bottom: screenBottom };

    // Current position (before offset)
    const baseX = this.config.target?.x ?? this.config.position?.x ?? this.x;
    const baseY = this.config.target?.y ?? this.config.position?.y ?? this.y;

    // Default direction: float up
    let dirX = 0;
    let dirY = -1;
    let offsetX = this.config.offset.x;
    let offsetY = this.config.offset.y;

    // Check vertical edges
    if (baseY + offsetY - margin < bounds.top) {
      // Too close to top - float down instead
      dirY = 1;
      offsetY = Math.abs(offsetY);
    } else if (baseY + offsetY + margin > bounds.bottom) {
      // Too close to bottom - float up (default)
      dirY = -1;
      offsetY = -Math.abs(offsetY);
    }

    // Check horizontal edges
    const textHalfWidth = this.textObj.width / 2;
    if (baseX + offsetX - textHalfWidth < bounds.left + margin) {
      // Too close to left edge - shift right
      dirX = 0.5;
      offsetX = textHalfWidth + margin;
    } else if (baseX + offsetX + textHalfWidth > bounds.right - margin) {
      // Too close to right edge - shift left
      dirX = -0.5;
      offsetX = -(textHalfWidth + margin);
    }

    // Update position with calculated offset
    this.x = baseX + offsetX;
    this.y = baseY + offsetY;

    // Store direction for animation
    this.floatDirection = { x: dirX, y: dirY };
  }

  /**
   * Start following target object
   */
  private startFollowing(): void {
    if (!this.config.target) return;

    // Update position every frame
    this.updateEvent = this.scene.time.addEvent({
      delay: 16, // ~60fps
      callback: () => {
        if (!this.config.target || !this.active) {
          this.updateEvent?.remove();
          return;
        }

        // Recalculate position (but keep float direction stable)
        const baseX = this.config.target.x;
        const baseY = this.config.target.y;

        // Apply original offset
        this.x = baseX + this.config.offset.x;
        this.y = baseY + this.config.offset.y;
      },
      loop: true,
    });
  }

  /**
   * Start float and fade animation
   * First holds visible, then floats and fades out
   */
  private startAnimation(): void {
    const floatDistance = 40;
    const holdTime = (this.config as any).holdTime ?? DEFAULT_CONFIG.holdTime;
    const fadeTime = this.config.duration - holdTime;

    // Start with a hold period (fully visible, no movement)
    this.scene.time.delayedCall(holdTime, () => {
      if (!this.active) return;

      // Then start the fade animation
      this.fadeOutTween = this.scene.tweens.add({
        targets: this,
        x: this.x + this.floatDirection.x * floatDistance,
        y: this.y + this.floatDirection.y * floatDistance,
        alpha: 0,
        scale: 1.1,
        duration: fadeTime,
        ease: 'Power2',
        onComplete: () => {
          this.destroy();
        },
      });
    });
  }

  /**
   * Cleanup
   */
  destroy(fromScene?: boolean): void {
    this.fadeOutTween?.stop();
    this.updateEvent?.remove();
    super.destroy(fromScene);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Static Factory Methods
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Show a floating notification
   */
  static show(scene: Phaser.Scene, config: FloatingNotificationConfig): FloatingNotification {
    return new FloatingNotification(scene, config);
  }

  /**
   * Show error notification
   */
  static error(scene: Phaser.Scene, text: string, options?: Partial<FloatingNotificationConfig>): FloatingNotification {
    return FloatingNotification.show(scene, { ...options, text, type: 'error' });
  }

  /**
   * Show warning notification
   */
  static warning(scene: Phaser.Scene, text: string, options?: Partial<FloatingNotificationConfig>): FloatingNotification {
    return FloatingNotification.show(scene, { ...options, text, type: 'warning' });
  }

  /**
   * Show info notification
   */
  static info(scene: Phaser.Scene, text: string, options?: Partial<FloatingNotificationConfig>): FloatingNotification {
    return FloatingNotification.show(scene, { ...options, text, type: 'info' });
  }

  /**
   * Show success notification
   */
  static success(scene: Phaser.Scene, text: string, options?: Partial<FloatingNotificationConfig>): FloatingNotification {
    return FloatingNotification.show(scene, { ...options, text, type: 'success' });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Game-specific Factory Methods (no background)
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Show biomatter notification (gold text, no background)
   * Used for showing biomoney gains like "$15"
   */
  static biomatter(scene: Phaser.Scene, text: string, options?: Partial<FloatingNotificationConfig>): FloatingNotification {
    return FloatingNotification.show(scene, { ...options, text, type: 'biomatter' });
  }

  /**
   * Show powerup notification (green text, no background)
   * Used for showing powerup collection like "+1 Health"
   */
  static powerup(scene: Phaser.Scene, text: string, options?: Partial<FloatingNotificationConfig>): FloatingNotification {
    return FloatingNotification.show(scene, { ...options, text, type: 'powerup' });
  }

  /**
   * Show damage notification (red text, no background)
   * Used for showing damage numbers
   */
  static damage(scene: Phaser.Scene, text: string, options?: Partial<FloatingNotificationConfig>): FloatingNotification {
    return FloatingNotification.show(scene, { ...options, text, type: 'damage' });
  }
}
